/**
 * Older proposed plans whose work is finished or replaced.
 *
 * A proposal is archived only when it is strictly older than 48 hours and the
 * loaded rows show that the work finished or a later plan replaced it. Age
 * alone leaves an unresolved owner decision where it is. Nothing here deletes
 * or updates a row. The Overseer waiting count uses the same rule in
 * supabase/functions/brunel-planner/index.ts.
 */
import type { Plan } from './data';

/** Strictly older than this. A proposal at exactly 48 hours still needs the owner. */
export const PROPOSAL_ARCHIVE_MAX_AGE_MS = 48 * 60 * 60 * 1000;

export type ProposalArchiveReason = 'finished' | 'superseded';

export interface ProposalArchiveRow {
  readonly plan: Plan;
  readonly reason: ProposalArchiveReason;
  readonly command: string;
}

export interface ProposalArchive {
  readonly archived: ReadonlyMap<string, ProposalArchiveReason>;
  /** Newest archived proposal first. The owner opens these; they are not removed. */
  readonly rows: readonly ProposalArchiveRow[];
}

const FINISHED_TASK_STATUSES = new Set(['completed', 'discarded']);
const SUPERSEDING_PLAN_STATUSES = new Set<Plan['status']>(['proposed', 'approved', 'rejected']);

/** PostgreSQL btrim default: ASCII spaces only, both ends. */
export function commandText(content: string | null | undefined): string {
  return (content ?? '').replace(/^ +| +$/g, '');
}

function uniquePlans(plans: readonly Plan[]): Plan[] {
  const byId = new Map<string, Plan>();
  for (const plan of plans) byId.set(plan.id, plan);
  return [...byId.values()];
}

function olderThanArchiveWindow(createdAt: string, nowMs: number): boolean {
  if (!Number.isFinite(nowMs)) return false;
  const created = Date.parse(createdAt);
  if (Number.isNaN(created)) return false;
  return nowMs - created > PROPOSAL_ARCHIVE_MAX_AGE_MS;
}

function missionFinished(missionId: string | null, tasksByMission: ReadonlyMap<string, readonly string[]>): boolean {
  if (missionId === null || missionId === '') return false;
  const statuses = tasksByMission.get(missionId);
  if (statuses === undefined || statuses.length === 0) return false;
  return statuses.every((status) => FINISHED_TASK_STATUSES.has(status));
}

/**
 * Proposed plans that leave the needs-you counts. `plans` may mix the live
 * proposed/failed window with plan history; the same id is read once.
 */
export function proposalArchive(input: {
  readonly plans: readonly Plan[];
  readonly commands: readonly { readonly id: string; readonly content: string | null }[];
  readonly tasks: readonly { readonly mission_id: string; readonly status: string }[];
  readonly nowMs: number;
}): ProposalArchive {
  const commandById = new Map(input.commands.map((command) => [command.id, commandText(command.content)]));
  const tasksByMission = new Map<string, string[]>();
  for (const task of input.tasks) {
    const held = tasksByMission.get(task.mission_id);
    if (held) held.push(task.status);
    else tasksByMission.set(task.mission_id, [task.status]);
  }
  const plans = uniquePlans(input.plans);
  const textOf = (plan: Plan): string => commandById.get(plan.command_id) ?? '';
  const archived = new Map<string, ProposalArchiveReason>();
  const rows: ProposalArchiveRow[] = [];

  for (const plan of plans) {
    if (plan.status !== 'proposed') continue;
    if (!olderThanArchiveWindow(plan.created_at, input.nowMs)) continue;
    const text = textOf(plan);
    const created = Date.parse(plan.created_at);
    let superseded = plan.held_for === 'duplicate_command';
    let finished = false;
    for (const other of plans) {
      if (other.id === plan.id || other.project_id !== plan.project_id) continue;
      const sameText = text !== '' && textOf(other) === text;
      if (!sameText) continue;
      if (other.status === 'approved' && missionFinished(other.mission_id, tasksByMission)) finished = true;
      if (!SUPERSEDING_PLAN_STATUSES.has(other.status)) continue;
      const otherCreated = Date.parse(other.created_at);
      if (!Number.isNaN(created) && !Number.isNaN(otherCreated) && otherCreated > created) superseded = true;
    }
    let reason: ProposalArchiveReason | null = null;
    if (finished) reason = 'finished';
    else if (superseded) reason = 'superseded';
    if (reason === null) continue;
    archived.set(plan.id, reason);
    rows.push({ plan, reason, command: text });
  }

  rows.sort((left, right) => {
    const leftMs = Date.parse(left.plan.created_at);
    const rightMs = Date.parse(right.plan.created_at);
    if (Number.isNaN(leftMs) && Number.isNaN(rightMs)) return 0;
    if (Number.isNaN(leftMs)) return 1;
    if (Number.isNaN(rightMs)) return -1;
    return rightMs - leftMs;
  });
  return { archived, rows };
}

/** Proposed plans that still need an owner decision. Matches planAttention().waiting on this list. */
export function openProposalCount(plans: readonly Plan[], archived: ReadonlyMap<string, ProposalArchiveReason>): number {
  return plans.filter((plan) => plan.status === 'proposed' && !archived.has(plan.id)).length;
}

/** History and archive copy. The transcript adds the proposal summary in front of this. */
export function proposalArchiveNote(reason: ProposalArchiveReason): string {
  return reason === 'finished'
    ? 'Archived. The work finished.'
    : 'Archived. A later plan replaced this one.';
}

/** Transcript text for an archived proposal. It does not ask the owner to decide. */
export function proposalArchiveTranscript(plan: { readonly summary: string }, reason: ProposalArchiveReason): string {
  const summary = plan.summary.trim();
  const lead = summary === '' ? 'Archived proposal' : summary;
  const because = reason === 'finished'
    ? 'The work finished, so this proposal is in the archive.'
    : 'A later plan replaced this one, so this proposal is in the archive.';
  return `${lead}. ${because}`;
}

/** Command ids on loaded plans that the newest command window did not include. */
export function planCommandIdsMissing(
  plans: readonly { readonly command_id: string }[],
  commands: readonly { readonly id: string }[],
): string[] {
  const known = new Set(commands.map((command) => command.id));
  const missing: string[] = [];
  const seen = new Set<string>();
  for (const plan of plans) {
    const id = plan.command_id;
    if (id === '' || known.has(id) || seen.has(id)) continue;
    seen.add(id);
    missing.push(id);
  }
  return missing;
}

/** Keep the loaded command order, then append command texts the window had dropped. */
export function mergeOwnerCommands<T extends { readonly id: string }>(base: readonly T[], extra: readonly T[]): T[] {
  const seen = new Set(base.map((command) => command.id));
  const merged = [...base];
  for (const command of extra) {
    if (seen.has(command.id)) continue;
    seen.add(command.id);
    merged.push(command);
  }
  return merged;
}

/**
 * Load command text for plans whose commands fell outside the newest window.
 * A failed or empty extra read keeps the commands already loaded.
 */
export async function commandsCoveringPlans<T extends { readonly id: string }>(
  commands: readonly T[],
  plans: readonly { readonly command_id: string }[],
  loadByIds: (ids: readonly string[]) => Promise<{ readonly ok: true; readonly rows: readonly T[] } | { readonly ok: false }>,
): Promise<readonly T[]> {
  const missing = planCommandIdsMissing(plans, commands);
  if (missing.length === 0) return commands;
  try {
    const extra = await loadByIds(missing);
    if (!extra.ok) return commands;
    return mergeOwnerCommands(commands, extra.rows);
  } catch {
    return commands;
  }
}
